import React, { useEffect, useMemo, useState, useCallback } from "react";
import { MIN_CONTRACT_DATE } from "utils/date-limits";
import {
  Box,
  Grid,
  TextField,
  Autocomplete,
  Button,
  Collapse,
  IconButton,
  Paper,
  Typography,
  Chip,
  Divider,
} from "@mui/material";
import { IconFilter, IconX, IconChevronDown, IconChevronUp } from "@tabler/icons-react";
import SavedContractFilters from "components/contracts/SavedContractFilters";
import {
  parseContractFiltersSnapshot,
  EMPTY_CONTRACT_FILTERS,
  BRAZILIAN_UF_OPTIONS,
  parseMultiFilterValue,
  formatMultiFilterValue,
} from "utils/contract-filters";
import {
  asyncAutocompleteListboxProps,
  buildBankSearchUrl,
  buildCustomerSearchUrl,
  createAsyncEntityFilterOptions,
  findById,
  resolveSelectedByIds,
  sameEntityId,
  useAsyncAutocompleteInputState,
  useAsyncEntitySearch,
} from "utils/entity-autocomplete";

const FilterSection = ({ title, children }) => (
  <Box sx={{ mb: 2.5 }}>
    <Typography
      variant="subtitle2"
      color="text.secondary"
      sx={{ mb: 1.5, fontWeight: 600 }}
    >
      {title}
    </Typography>
    <Grid container spacing={2}>
      {children}
    </Grid>
  </Box>
);

const DateRangeFilter = ({
  fromLabel,
  toLabel,
  fromValue,
  toValue,
  onFromChange,
  onToChange,
  gridSize = 6,
}) => (
  <Grid item xs={12} md={gridSize}>
    <Box sx={{ display: "flex", gap: 2, flexWrap: "nowrap" }}>
      <TextField
        label={fromLabel}
        type="date"
        value={fromValue || ""}
        onChange={(e) => onFromChange(e.target.value)}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: MIN_CONTRACT_DATE }}
        sx={{ minWidth: 0 }}
      />
      <TextField
        label={toLabel}
        type="date"
        value={toValue || ""}
        onChange={(e) => onToChange(e.target.value)}
        fullWidth
        size="small"
        InputLabelProps={{ shrink: true }}
        inputProps={{ min: MIN_CONTRACT_DATE }}
        sx={{ minWidth: 0 }}
      />
    </Box>
  </Grid>
);

const ContractsFilters = ({
  customers = [],
  banks = [],
  contractTypes = [],
  lawyers = [],
  salesConsultants = [],
  users: usersProp = [],
  contractCategories = [],
  contractStatuses = [],
  filters = {},
  onFiltersChange,
  onClearFilters,
  searchTerm = "",
  importedStatusFilter = "all",
  missingDataFilter = "all",
  showDeletedContracts = false,
  onApplySavedFilter,
  onDefaultFilterLoaded,
  onInitialFiltersReady,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [localFilters, setLocalFilters] = useState(filters);
  const [selectedCustomerEntities, setSelectedCustomerEntities] = useState([]);
  const {
    inputValue: customerInputValue,
    bindInputChange: bindCustomerInputChange,
    clearInput: clearCustomerInput,
  } = useAsyncAutocompleteInputState();
  const {
    inputValue: bankInputValue,
    bindInputChange: bindBankInputChange,
    clearInput: clearBankInput,
  } = useAsyncAutocompleteInputState();

  const selectedCustomers = useMemo(
    () =>
      resolveSelectedByIds(
        localFilters.customer_id,
        selectedCustomerEntities,
        customers
      ),
    [localFilters.customer_id, selectedCustomerEntities, customers]
  );

  const selectedBanks = useMemo(
    () => resolveSelectedByIds(localFilters.bank_id, banks),
    [localFilters.bank_id, banks]
  );

  const {
    options: customerOptions,
    loading: customersLoading,
    search: searchCustomers,
    listboxKey: customerListboxKey,
  } = useAsyncEntitySearch({
    buildUrl: buildCustomerSearchUrl,
    selectedEntities: selectedCustomers,
  });

  const {
    options: bankOptions,
    loading: banksLoading,
    search: searchBanks,
    listboxKey: bankListboxKey,
  } = useAsyncEntitySearch({
    buildUrl: buildBankSearchUrl,
    selectedEntities: selectedBanks,
  });

  const selectedCustomerValue = useMemo(
    () =>
      resolveSelectedByIds(
        localFilters.customer_id,
        selectedCustomerEntities,
        customerOptions,
        customers
      ),
    [localFilters.customer_id, selectedCustomerEntities, customerOptions, customers]
  );

  const selectedBankValue = useMemo(
    () => resolveSelectedByIds(localFilters.bank_id, bankOptions, banks),
    [localFilters.bank_id, bankOptions, banks]
  );

  const customerFilterOptions = useMemo(
    () => createAsyncEntityFilterOptions(customerInputValue),
    [customerInputValue]
  );

  const bankFilterOptions = useMemo(
    () => createAsyncEntityFilterOptions(bankInputValue),
    [bankInputValue]
  );

  useEffect(() => {
    setLocalFilters(filters);
  }, [filters]);

  useEffect(() => {
    const ids = parseMultiFilterValue(localFilters.customer_id);
    if (!ids.length) {
      setSelectedCustomerEntities([]);
      return;
    }

    setSelectedCustomerEntities((prev) => {
      const resolved = resolveSelectedByIds(localFilters.customer_id, prev, customers);
      return resolved.filter((customer) =>
        ids.some((id) => sameEntityId(id, customer.id))
      );
    });

    const resolved = resolveSelectedByIds(localFilters.customer_id, customers);
    const missingIds = ids.filter((id) => !findById(resolved, id));
    if (!missingIds.length) {
      return undefined;
    }

    let cancelled = false;
    Promise.all(
      missingIds.map((id) =>
        fetch(`/backend/customers/${id}/`, { credentials: "include" })
          .then((res) => (res.ok ? res.json() : null))
          .catch(() => null)
      )
    ).then((results) => {
      if (cancelled) return;
      const fetched = results.filter((customer) => customer?.id);
      setSelectedCustomerEntities((prev) => {
        const merged = [...prev];
        for (const customer of fetched) {
          if (!findById(merged, customer.id)) {
            merged.push(customer);
          }
        }
        return merged.filter((customer) =>
          ids.some((id) => sameEntityId(id, customer.id))
        );
      });
    });

    return () => {
      cancelled = true;
    };
  }, [localFilters.customer_id, customers]);
  
  // Garante que users seja sempre um array
  const users = Array.isArray(usersProp) ? usersProp : [];

  const handleFilterChange = (key, value) => {
    const newFilters = { ...localFilters, [key]: value || null };
    setLocalFilters(newFilters);
  };

  const handleApplyFilters = () => {
    onFiltersChange(localFilters);
  };

  const handleApplySavedSnapshot = useCallback((filtersData) => {
    const parsed = parseContractFiltersSnapshot(filtersData);
    setLocalFilters(parsed.filters);
    onApplySavedFilter?.(parsed);
  }, [onApplySavedFilter]);

  const handleClearFilters = () => {
    setLocalFilters({ ...EMPTY_CONTRACT_FILTERS });
    clearCustomerInput();
    clearBankInput();
    onClearFilters();
  };

  const booleanOptions = [
    { label: "Sim", value: "true" },
    { label: "Não", value: "false" },
  ];

  const irpfOptions = [
    { label: "Declara IRPF", value: "true" },
    { label: "Isento de IRPF", value: "false" },
  ];

  const customerDocumentsStatusOptions = [
    { label: "Atualizado", value: "ATUALIZADO" },
    { label: "Desatualizado", value: "DESATUALIZADO" },
  ];

  const getMultiOptionSelection = (filterKey, options, valueKey = "value") => {
    const values = parseMultiFilterValue(localFilters[filterKey]);
    return options.filter((option) => values.includes(String(option[valueKey])));
  };

  const handleMultiOptionChange = (filterKey, newValue, valueKey = "value") => {
    handleFilterChange(
      filterKey,
      formatMultiFilterValue(newValue.map((item) => item[valueKey]))
    );
  };

  const getMultiEntitySelection = (filterKey, ...lists) =>
    resolveSelectedByIds(localFilters[filterKey], ...lists);

  const handleMultiEntityChange = (filterKey, newValue, onEntitiesChange) => {
    onEntitiesChange?.(newValue);
    handleFilterChange(
      filterKey,
      formatMultiFilterValue(newValue.map((item) => item.id))
    );
  };

  const getUserLabel = (option) => {
    if (option.first_name || option.last_name) {
      return `${option.first_name || ""} ${option.last_name || ""}`.trim() || option.username;
    }
    return option.username || "";
  };

  const renderOptionTags = (getLabel) => (value, getTagProps) =>
    value.map((option, index) => (
      <Chip
        label={getLabel(option)}
        size="small"
        {...getTagProps({ index })}
      />
    ));

  const activeFiltersCount = Object.values(localFilters).filter(
    (v) => v !== null && v !== "" && v !== undefined
  ).length;

  return (
    <Box sx={{ mb: 2 }}>
      <Paper
        elevation={2}
        sx={{
          p: 2,
          borderRadius: 2,
          backgroundColor: expanded ? "background.paper" : "transparent",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            cursor: "pointer",
          }}
          onClick={() => setExpanded(!expanded)}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <IconFilter stroke={1.5} size="1.2rem" />
            <Typography variant="h6">Filtros Avançados</Typography>
            {activeFiltersCount > 0 && (
              <Chip
                label={activeFiltersCount}
                size="small"
                color="primary"
                sx={{ ml: 1 }}
              />
            )}
          </Box>
          <IconButton size="small">
            {expanded ? (
              <IconChevronUp stroke={1.5} size="1.2rem" />
            ) : (
              <IconChevronDown stroke={1.5} size="1.2rem" />
            )}
          </IconButton>
        </Box>

        <Collapse in={expanded}>
          <Box sx={{ mt: 3 }}>
            <SavedContractFilters
              embedded
              filters={localFilters}
              searchTerm={searchTerm}
              importedStatusFilter={importedStatusFilter}
              missingDataFilter={missingDataFilter}
              showDeletedContracts={showDeletedContracts}
              onApplySnapshot={handleApplySavedSnapshot}
              onDefaultLoaded={onDefaultFilterLoaded}
              onInitialLoadComplete={onInitialFiltersReady}
            />
            <Divider sx={{ my: 3 }} />

            <FilterSection title="Cliente">
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  multiple
                  options={customerOptions}
                  getOptionLabel={(option) => option.name || ""}
                  value={selectedCustomerValue}
                  inputValue={customerInputValue}
                  onChange={(event, newValue, reason) => {
                    handleMultiEntityChange(
                      "customer_id",
                      newValue,
                      setSelectedCustomerEntities
                    );
                    if (reason === "selectOption") {
                      clearCustomerInput();
                    }
                  }}
                  onInputChange={bindCustomerInputChange(searchCustomers)}
                  onOpen={() => searchCustomers(customerInputValue.trim())}
                  loading={customersLoading}
                  filterOptions={customerFilterOptions}
                  ListboxProps={asyncAutocompleteListboxProps(customerListboxKey)}
                  isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                  renderTags={renderOptionTags((option) => option.name || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Cliente"
                      placeholder="Selecione os clientes..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  label="CPF do Cliente"
                  value={localFilters.customer_cpf || ""}
                  onChange={(e) => {
                    const raw = e.target.value.replace(/\D/g, "").slice(0, 11);
                    const formatted = raw
                      .replace(/(\d{3})(\d)/, "$1.$2")
                      .replace(/(\d{3})\.(\d{3})(\d)/, "$1.$2.$3")
                      .replace(/(\d{3})\.(\d{3})\.(\d{3})(\d)/, "$1.$2.$3-$4");
                    handleFilterChange("customer_cpf", formatted || null);
                  }}
                  fullWidth
                  size="small"
                  placeholder="000.000.000-00"
                  inputProps={{ maxLength: 14 }}
                />
              </Grid>

              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  options={irpfOptions}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    irpfOptions.find((o) => o.value === localFilters.declares_irpf) ||
                    null
                  }
                  onChange={(event, newValue) =>
                    handleFilterChange("declares_irpf", newValue?.value)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="IRPF" fullWidth />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={customerDocumentsStatusOptions}
                  getOptionLabel={(option) => option.label || ""}
                  value={getMultiOptionSelection(
                    "customer_documents_update_status",
                    customerDocumentsStatusOptions
                  )}
                  onChange={(event, newValue) =>
                    handleMultiOptionChange(
                      "customer_documents_update_status",
                      newValue
                    )
                  }
                  renderTags={renderOptionTags((option) => option.label || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status dos documentos"
                      placeholder="Selecione os status..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>

              <DateRangeFilter
                fromLabel="Atual. documentos (Início)"
                toLabel="Atual. documentos (Fim)"
                fromValue={localFilters.customer_documents_last_updated_at_from}
                toValue={localFilters.customer_documents_last_updated_at_to}
                onFromChange={(value) =>
                  handleFilterChange("customer_documents_last_updated_at_from", value)
                }
                onToChange={(value) =>
                  handleFilterChange("customer_documents_last_updated_at_to", value)
                }
                gridSize={9}
              />

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={BRAZILIAN_UF_OPTIONS}
                  getOptionLabel={(option) => option.label || ""}
                  value={getMultiOptionSelection("customer_state", BRAZILIAN_UF_OPTIONS)}
                  onChange={(event, newValue) =>
                    handleMultiOptionChange("customer_state", newValue)
                  }
                  renderTags={renderOptionTags((option) => option.value || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="UF"
                      placeholder="Selecione as UFs..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>
            </FilterSection>

            <Divider sx={{ mb: 2.5 }} />

            <FilterSection title="Contrato">
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={bankOptions}
                  getOptionLabel={(option) => option.name || ""}
                  value={selectedBankValue}
                  inputValue={bankInputValue}
                  onChange={(event, newValue, reason) => {
                    handleMultiEntityChange("bank_id", newValue);
                    if (reason === "selectOption") {
                      clearBankInput();
                    }
                  }}
                  onInputChange={bindBankInputChange(searchBanks)}
                  onOpen={() => searchBanks(bankInputValue.trim())}
                  loading={banksLoading}
                  filterOptions={bankFilterOptions}
                  ListboxProps={asyncAutocompleteListboxProps(bankListboxKey)}
                  isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                  renderTags={renderOptionTags((option) => option.name || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Banco"
                      placeholder="Selecione os bancos..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={contractStatuses}
                  getOptionLabel={(option) => option.label || ""}
                  value={getMultiOptionSelection("status", contractStatuses)}
                  onChange={(event, newValue) =>
                    handleMultiOptionChange("status", newValue)
                  }
                  renderTags={renderOptionTags((option) => option.label || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Status"
                      placeholder="Selecione os status..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={contractCategories}
                  getOptionLabel={(option) => option.label || ""}
                  value={getMultiOptionSelection("category", contractCategories)}
                  onChange={(event, newValue) =>
                    handleMultiOptionChange("category", newValue)
                  }
                  renderTags={renderOptionTags((option) => option.label || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Categoria"
                      placeholder="Selecione as categorias..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={contractTypes}
                  getOptionLabel={(option) => option.name || ""}
                  value={getMultiEntitySelection("contract_type_id", contractTypes)}
                  onChange={(event, newValue) =>
                    handleMultiEntityChange("contract_type_id", newValue)
                  }
                  isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                  renderTags={renderOptionTags((option) => option.name || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Tipo de Contrato"
                      placeholder="Selecione os tipos..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Nº Contrato"
                  value={localFilters.number || ""}
                  onChange={(e) =>
                    handleFilterChange("number", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <TextField
                  label="Nº Processo"
                  value={localFilters.process_number || ""}
                  onChange={(e) =>
                    handleFilterChange("process_number", e.target.value)
                  }
                  fullWidth
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={booleanOptions}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    booleanOptions.find((o) => o.value === localFilters.has_ajg) ||
                    null
                  }
                  onChange={(event, newValue) =>
                    handleFilterChange("has_ajg", newValue?.value)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Tem AJG" fullWidth />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={booleanOptions}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    booleanOptions.find((o) => o.value === localFilters.has_after_sales_video) ||
                    null
                  }
                  onChange={(event, newValue) =>
                    handleFilterChange("has_after_sales_video", newValue?.value)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Tem vídeo pós vendas" fullWidth />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  options={booleanOptions}
                  getOptionLabel={(option) => option.label || ""}
                  value={
                    booleanOptions.find(
                      (o) => o.value === localFilters.is_rate_below_abusive_threshold
                    ) || null
                  }
                  onChange={(event, newValue) =>
                    handleFilterChange("is_rate_below_abusive_threshold", newValue?.value)
                  }
                  renderInput={(params) => (
                    <TextField {...params} label="Taxa não abusiva" fullWidth />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={BRAZILIAN_UF_OPTIONS}
                  getOptionLabel={(option) => option.label || ""}
                  value={getMultiOptionSelection("state", BRAZILIAN_UF_OPTIONS)}
                  onChange={(event, newValue) =>
                    handleMultiOptionChange("state", newValue)
                  }
                  renderTags={renderOptionTags((option) => option.value || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="UF"
                      placeholder="Selecione as UFs..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>
            </FilterSection>

            <Divider sx={{ mb: 2.5 }} />

            <FilterSection title="Equipe">
              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={lawyers}
                  getOptionLabel={(option) => option.name || ""}
                  value={getMultiEntitySelection("lawyer_id", lawyers)}
                  onChange={(event, newValue) =>
                    handleMultiEntityChange("lawyer_id", newValue)
                  }
                  isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                  renderTags={renderOptionTags((option) => option.name || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Advogado"
                      placeholder="Selecione os advogados..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={lawyers}
                  getOptionLabel={(option) => option.name || ""}
                  value={getMultiEntitySelection("lawyer_wallet_id", lawyers)}
                  onChange={(event, newValue) =>
                    handleMultiEntityChange("lawyer_wallet_id", newValue)
                  }
                  isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                  renderTags={renderOptionTags((option) => option.name || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Carteira"
                      placeholder="Selecione as carteiras..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={salesConsultants}
                  getOptionLabel={(option) => option.name || ""}
                  value={getMultiEntitySelection("sales_consultant_id", salesConsultants)}
                  onChange={(event, newValue) =>
                    handleMultiEntityChange("sales_consultant_id", newValue)
                  }
                  isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                  renderTags={renderOptionTags((option) => option.name || "")}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Consultor de Vendas"
                      placeholder="Selecione os consultores..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>

              <Grid item xs={12} sm={6} md={3}>
                <Autocomplete
                  multiple
                  options={users}
                  getOptionLabel={getUserLabel}
                  value={getMultiEntitySelection("responsible_user_id", users)}
                  onChange={(event, newValue) =>
                    handleMultiEntityChange("responsible_user_id", newValue)
                  }
                  isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                  renderTags={renderOptionTags(getUserLabel)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Responsável"
                      placeholder="Selecione os responsáveis..."
                      fullWidth
                    />
                  )}
                  size="small"
                />
              </Grid>
            </FilterSection>

            <Divider sx={{ mb: 2.5 }} />

            <FilterSection title="Datas">
              <DateRangeFilter
                fromLabel="Data Fechamento (Início)"
                toLabel="Data Fechamento (Fim)"
                fromValue={localFilters.date_start}
                toValue={localFilters.date_end}
                onFromChange={(value) => handleFilterChange("date_start", value)}
                onToChange={(value) => handleFilterChange("date_end", value)}
              />

              <DateRangeFilter
                fromLabel="Data Protocolo (Início)"
                toLabel="Data Protocolo (Fim)"
                fromValue={localFilters.filed_at_start}
                toValue={localFilters.filed_at_end}
                onFromChange={(value) => handleFilterChange("filed_at_start", value)}
                onToChange={(value) => handleFilterChange("filed_at_end", value)}
              />

              <DateRangeFilter
                fromLabel="Ajuizar em (Início)"
                toLabel="Ajuizar em (Fim)"
                fromValue={localFilters.file_at_start}
                toValue={localFilters.file_at_end}
                onFromChange={(value) => handleFilterChange("file_at_start", value)}
                onToChange={(value) => handleFilterChange("file_at_end", value)}
              />
            </FilterSection>

            {showDeletedContracts && (
              <>
                <Divider sx={{ mb: 2.5 }} />
                <FilterSection title="Exclusão">
                  <DateRangeFilter
                    fromLabel="Excluído em (Início)"
                    toLabel="Excluído em (Fim)"
                    fromValue={localFilters.deleted_at_start}
                    toValue={localFilters.deleted_at_end}
                    onFromChange={(value) => handleFilterChange("deleted_at_start", value)}
                    onToChange={(value) => handleFilterChange("deleted_at_end", value)}
                    gridSize={6}
                  />

                  <Grid item xs={12} sm={6} md={3}>
                    <Autocomplete
                      multiple
                      options={users}
                      getOptionLabel={getUserLabel}
                      value={getMultiEntitySelection("deleted_by_id", users)}
                      onChange={(event, newValue) =>
                        handleMultiEntityChange("deleted_by_id", newValue)
                      }
                      isOptionEqualToValue={(option, value) => sameEntityId(option?.id, value?.id)}
                      renderTags={renderOptionTags(getUserLabel)}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Excluído por"
                          placeholder="Selecione os usuários..."
                          fullWidth
                        />
                      )}
                      size="small"
                    />
                  </Grid>
                </FilterSection>
              </>
            )}

            {/* Botões de Ação */}
            <Box sx={{ display: "flex", gap: 2, mt: 3, justifyContent: "flex-end" }}>
              <Button
                variant="outlined"
                onClick={handleClearFilters}
                startIcon={<IconX stroke={1.5} size="1rem" />}
              >
                Limpar Filtros
              </Button>
              <Button
                variant="contained"
                onClick={handleApplyFilters}
                startIcon={<IconFilter stroke={1.5} size="1rem" />}
              >
                Aplicar Filtros
              </Button>
            </Box>
          </Box>
        </Collapse>
      </Paper>
    </Box>
  );
};

export default ContractsFilters;

